type MktMenuItem = {
	label: string;
	description: string;
	href: string;
	icon: string;
	iconType?: 'fab' | 'fad' | 'fajd' | 'fajf' | 'fajr' | 'far' | 'fas' | 'fawsb';
	target?: '_blank';
};

type MktMenuGroup = {
	label: string;
	href?: string;
	target?: '_blank';
	items: MktMenuItem[];
};

type MktMenuDefinition = {
	value: string;
	label: string;
	groups: MktMenuGroup[];
};

const productMenu: MktMenuDefinition = {
	value: 'product',
	label: 'Product',
	groups: [
		{
			label: 'Overview',
			href: '/product',
			items: [
				{
					label: 'Product overview',
					description: 'See how Playbooks turns ideas into intelligent products.',
					href: '/product',
					icon: 'cube',
				},
				{
					label: 'Discover & organize',
					description: 'Find inspiration, then save and curate what matters.',
					href: '/product#discover',
					icon: 'compass',
				},
			],
		},
		{
			label: 'Build',
			items: [
				{
					label: 'Publish & deploy',
					description: 'Turn repositories into secure, hosted software.',
					href: '/product#deploy',
					icon: 'rocket',
				},
				{
					label: 'Showcase & collaborate',
					description: 'Present your work and gather feedback in context.',
					href: '/product#showcase',
					icon: 'user-group',
				},
				{
					label: 'Monetize expertise',
					description: 'Earn from the software and knowledge you contribute.',
					href: '/product#monetize',
					icon: 'money-bill-wave',
				},
			],
		},
		{
			label: 'Access',
			items: [
				{
					label: 'Developer access',
					description: 'Connect through APIs, the CLI, and shareable links.',
					href: '/product#access',
					icon: 'terminal',
				},
				{
					label: 'Agent workflows',
					description: 'Build with MCP and tools designed for coding agents.',
					href: '/product#agents',
					icon: 'brain-circuit',
				},
			],
		},
	],
};

const exploreMenu: MktMenuDefinition = {
	value: 'explore',
	label: 'Explore',
	groups: [
		{
			label: 'Discover',
			href: '/explore',
			items: [
				{
					label: 'Recently added',
					description: 'See the newest projects added to the marketplace.',
					href: '/explore/projects?view=recent',
					icon: 'clock',
				},
				{
					label: 'Most remixed',
					description: 'Browse the projects remixed most by the community.',
					href: '/explore/projects?view=popular',
					icon: 'rotate',
				},
				{
					label: "Editors' picks",
					description: 'Explore standout projects selected by the Playbooks team.',
					href: '/explore/projects?view=featured',
					icon: 'sparkles',
				},
			],
		},
		{
			label: 'Curate',
			items: [
				{
					label: 'Agents',
					description: 'Discover agents available to remix from the marketplace.',
					href: '/explore?type=agent',
					icon: 'robot',
				},
				{
					label: 'Projects',
					description: 'Explore public projects available in the marketplace.',
					href: '/explore?type=project',
					icon: 'folder',
				},
				{
					label: 'Collections',
					description: 'Browse curated sets of marketplace projects.',
					href: '/explore/collections',
					icon: 'layer-group',
				},
			],
		},
	],
};

const resourcesMenu: MktMenuDefinition = {
	value: 'resources',
	label: 'Resources',
	groups: [
		{
			label: 'Learn',
			href: '/docs',
			items: [
				{
					label: 'Documentation',
					description: 'Learn the platform and its core workflows.',
					href: '/docs',
					icon: 'book-open',
				},
				{
					label: 'API reference',
					description: 'Explore endpoints, schemas, and implementation details.',
					href: '/reference',
					icon: 'globe',
				},
				{
					label: 'CLI',
					description: 'Use Playbooks directly from your terminal.',
					href: '/cli',
					icon: 'terminal',
				},
				{
					label: 'MCP',
					description: 'Connect Playbooks to compatible coding agents.',
					href: '/docs/mcp/overview',
					icon: 'brain-circuit',
				},
			],
		},
		{
			label: 'Editorial',
			href: '/blog',
			items: [
				{
					label: 'Blog',
					description: 'Read ideas, guides, and stories from Playbooks.',
					href: '/blog',
					icon: 'newspaper',
				},
				{
					label: 'Changelog',
					description: 'Read about product updates and shipped improvements.',
					href: '/blog/changelog',
					icon: 'code-commit',
				},
				{
					label: 'Engineering',
					description: 'Go behind the scenes with the people building Playbooks.',
					href: '/blog/engineering',
					icon: 'code',
				},
				{
					label: 'Perspectives',
					description: 'Explore our point of view on intelligent software.',
					href: '/blog/perspectives',
					icon: 'lightbulb',
				},
			],
		},
		{
			label: 'Community',
			items: [
				{
					label: 'GitHub',
					description: 'Explore Playbooks projects and open-source work.',
					href: 'https://github.com/playbooks-hq',
					icon: 'github',
					iconType: 'fab',
					target: '_blank',
				},
				{
					label: 'Twitter',
					description: 'Follow product updates and ideas from Playbooks.',
					href: 'https://x.com/playbooks_hq',
					icon: 'x-twitter',
					iconType: 'fab',
					target: '_blank',
				},
				{
					label: 'Slack',
					description: 'Join the community and talk with other builders.',
					href: 'https://slack.playbooks.ai',
					icon: 'slack',
					iconType: 'fab',
					target: '_blank',
				},
				{
					label: 'YouTube',
					description: 'Watch product guides, demos, and updates from Playbooks.',
					href: 'https://www.youtube.com/@playbookshq',
					icon: 'youtube',
					iconType: 'fab',
					target: '_blank',
				},
			],
		},
	],
};

const companyMenu: MktMenuDefinition = {
	value: 'company',
	label: 'Company',
	groups: [
		{
			label: 'Playbooks',
			href: '/about',
			items: [
				{
					label: 'About',
					description: 'Learn about Playbooks and the ideas behind it.',
					href: '/about',
					icon: 'circle-info',
				},
				{
					label: 'Contact',
					description: 'Talk with us about your workspace, product, or partnership.',
					href: '/contact',
					icon: 'headset',
				},
			],
		},
		{
			label: 'Trust',
			href: '/legal',
			items: [
				{
					label: 'Status',
					description: 'Check the availability of Playbooks services.',
					href: 'https://playbooks.betteruptime.com',
					icon: 'circle-check',
					target: '_blank',
				},
				{
					label: 'Legal',
					description: 'Review Playbooks terms, privacy, and legal policies.',
					href: '/legal',
					icon: 'file-contract',
				},
			],
		},
	],
};

const marketingMenus = [companyMenu, productMenu, exploreMenu, resourcesMenu];

export { marketingMenus };
export type { MktMenuDefinition, MktMenuGroup, MktMenuItem };
